// src/components/Admin/QuizComponents/QuizItem/QuizItem.js
import React, { useState, useEffect } from "react";
import {
  Icon,
  Confirm,
  Button,
  Modal,
  Header,
  Table,
  Loader,
} from "semantic-ui-react";
import { BasicModal } from "../../../Shared";
import { QuizForm } from "../QuizForm/QuizForm";
import { QuizApi } from "../../../../api/quiz";
import { TeacherAPI } from "../../../../api/teacherAPI";
import { useAuth } from "../../../../hooks";
import "./QuizItem.scss";

const quizController = new QuizApi();
const teacherController = new TeacherAPI();

export function QuizItem({ quiz, onReload }) {
  const { accessToken, user } = useAuth();
  const teacherId = user.user.teacher;

  // Estados para CRUD de Quizz
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  // Estados para ver ranking
  const [showViewModal, setShowViewModal] = useState(false);
  const [classNumbers, setClassNumbers] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [rankingData, setRankingData] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);

  // Estados para ver detalle por alumno
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailResults, setDetailResults] = useState(null);

  // 1️⃣ Al montar, cargamos los classnumber del teacher
  useEffect(() => {
    (async () => {
      setLoadingClasses(true);
      try {
        const res = await teacherController.getTeacherById(
          accessToken,
          teacherId
        );
        const roles = Array.isArray(res.teacher.classrole)
          ? res.teacher.classrole
          : [];
        setClassNumbers(roles.map((r) => r.classnumber));
      } catch (err) {
        console.error("Error cargando clases del teacher:", err);
      } finally {
        setLoadingClasses(false);
      }
    })();
  }, [accessToken, teacherId]);

  // Handlers CRUD
  const onOpenCloseEdit = () => setShowEditModal((prev) => !prev);
  const onOpenCloseDelete = () =>
    setShowConfirmDelete((prev) => !prev);
  const onDelete = async () => {
    await quizController.deleteQuiz(accessToken, quiz._id);
    onReload();
    onOpenCloseDelete();
  };

  // Handler para mostrar ranking
  const onOpenCloseView = () => {
    setShowViewModal((prev) => !prev);
    setSelectedClass("");
    setRankingData([]);
  };

  // 2️⃣ Cuando elige una clase, traemos ranking
  const fetchRankingForClass = async (classnumber) => {
    setSelectedClass(classnumber);
    setLoadingRanking(true);
    try {
      const res = await quizController.getRankingResults(
        accessToken,
        teacherId,
        quiz._id,
        classnumber
      );
      setRankingData(res.ranking || []);
    } catch (err) {
      console.error("Error obteniendo ranking:", err);
      setRankingData([]);
    } finally {
      setLoadingRanking(false);
    }
  };

  // 3️⃣ Abrir detalle de un alumno concreto
  const fetchStudentDetail = async (studentId) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await quizController.getDetailedStudentResult(
        accessToken,
        studentId,
        quiz._id
      );
      const data = res.result;
      const fullDetails = quiz.questions.map((q) => {
        const wrong = data.incorrectDetails.find(
          (d) => d.question === q.question
        );
        const selected = wrong ? wrong.selected : q.correct;
        const correctAnswer = q.correct;
        const isCorrect = selected === correctAnswer;
        return {
          question: q.question,
          selected,
          correctAnswer,
          isCorrect,
        };
      });
      setDetailResults({
        timeTaken: data.timeTaken,
        details: fullDetails,
      });
      setShowDetailModal(true);
    } catch (err) {
      console.error("Error obteniendo detalle del alumno:", err);
      setDetailError(err.message || "Error desconocido");
    } finally {
      setDetailLoading(false);
    }
  };
  const onCloseDetail = () => {
    setShowDetailModal(false);
    setDetailResults(null);
  };

  return (
    <>
      <div className="e-card-quiz">
        <div className="quiz-info">
          <h3 className="quiz-title">{quiz.name}</h3>
          <p className="quiz-description">{quiz.description}</p>
          <div className="quiz-buttons">
            <Button
              icon
              className="btn-icon e-button-edit"
              onClick={onOpenCloseEdit}
            >
              <Icon name="pencil" />
            </Button>
            <Button
              icon
              className="btn-icon e-button-view"
              onClick={onOpenCloseView}
            >
              <Icon name="eye" />
            </Button>
            <Button
              icon
              className="btn-icon btn-icon--danger e-button-delete"
              onClick={onOpenCloseDelete}
            >
              <Icon name="trash" />
            </Button>
            
          </div>
        </div>
      </div>

      {/* — Modal de edición — */}
      <BasicModal
        show={showEditModal}
        close={onOpenCloseEdit}
        title={`Editar Quizz: ${quiz.name}`}
        size="large"
      >
        <QuizForm
          quiz={quiz}
          onClose={onOpenCloseEdit}
          onReload={onReload}
        />
      </BasicModal>

      {/* — Confirm delete — */}
      <Confirm
        open={showConfirmDelete}
        onCancel={onOpenCloseDelete}
        onConfirm={onDelete}
        content={`¿Eliminar el quizz "${quiz.name}"?`}
        size="mini"
      />

      {/* — Modal ver ranking — */}
      <Modal
        open={showViewModal}
        onClose={onOpenCloseView}
        size="small"
        className="modal-view-ranking"
      >
        <Header icon="chart bar" content={`Ranking: ${quiz.name}`} />
        <Modal.Content>
          {loadingClasses ? (
            <Loader active inline="centered">
              Cargando clases...
            </Loader>
          ) : !selectedClass ? (
            <div className="class-list-buttons">
              {classNumbers.map((cls) => (
                <Button
                  key={cls}
                  onClick={() => fetchRankingForClass(cls)}
                  className="class-btn"
                >
                  {cls}
                </Button>
              ))}
            </div>
          ) : loadingRanking ? (
            <Loader active inline="centered">
              Cargando ranking...
            </Loader>
          ) : (
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>#</Table.HeaderCell>
                  <Table.HeaderCell>Alumno</Table.HeaderCell>
                  <Table.HeaderCell>Correctas</Table.HeaderCell>
                  <Table.HeaderCell>Incorrectas</Table.HeaderCell>
                  <Table.HeaderCell>Tiempo (s)</Table.HeaderCell>
                  <Table.HeaderCell>Detalle</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rankingData.map((item, idx) => (
                  <Table.Row key={item.studentId}>
                    <Table.Cell>{idx + 1}</Table.Cell>
                    <Table.Cell>{item.studentName}</Table.Cell>
                    <Table.Cell>{item.totalCorrect}</Table.Cell>
                    <Table.Cell>{item.totalIncorrect}</Table.Cell>
                    <Table.Cell>{item.timeTaken}</Table.Cell>
                    <Table.Cell>
                      <Button
                        icon
                        className="btn-icon e-button-view-detail"
                        onClick={() =>
                          fetchStudentDetail(item.studentId)
                        }
                        >
                        <Icon name="eye" />
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            className="btn-icon btn-icon--cancel"
            onClick={onOpenCloseView}
          >
            <Icon name="close" /> Cerrar
          </Button>
        </Modal.Actions>
      </Modal>

      {/* — Modal detalle por alumno — */}
      <Modal
        open={showDetailModal}
        onClose={onCloseDetail}
        size="large"
        closeIcon
        className="modal-student-detail"
      >
        <Header icon="info circle" content="Detalle de respuestas" />
        <Modal.Content scrolling>
          {detailLoading ? (
            <Loader active inline="centered">
              Cargando detalle...
            </Loader>
          ) : detailError ? (
            <p style={{ color: "red" }}>{detailError}</p>
          ) : detailResults ? (
            <>
              <p>
                <strong>Tiempo empleado:</strong>{" "}
                {detailResults.timeTaken}s
              </p>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>#</Table.HeaderCell>
                    <Table.HeaderCell>Pregunta</Table.HeaderCell>
                    <Table.HeaderCell>Tu respuesta</Table.HeaderCell>
                    <Table.HeaderCell>Respuesta correcta</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {detailResults.details.map((d, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{i + 1}</Table.Cell>
                      <Table.Cell>{d.question}</Table.Cell>
                      <Table.Cell
                        style={{
                          backgroundColor: d.isCorrect
                            ? "#d4edda"
                            : "#f8d7da",
                        }}
                      >
                        {d.selected}
                      </Table.Cell>
                      <Table.Cell>{d.correctAnswer}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Button
            className="btn-icon btn-icon--cancel"
            onClick={onCloseDetail}
          >
            <Icon name="close" /> Cerrar
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
